import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { trackPageConversion } from '../lib/analytics';
import { ArrowLeft, Star, Gift, CheckCircle2, AlertCircle, Calendar, ArrowRight, CreditCard, Wallet, Users } from 'lucide-react';

const StarFilled = ({ className, size }: { className?: string, size?: number }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size || 24}
    height={size || 24}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
  >
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

interface Campaign {
  id: string;
  bank_id: string;
  bank_name: string;
  title: string;
  description: string;
  campaign_image: string;
  is_hero: boolean;
  offer_details: string[];
  end_date: string;
  terms: string[];
  display_order: number;
  is_active: boolean;
}

interface Bank {
  id: string;
  bank_id: string;
  bank_name: string;
  logo_url: string;
  slogan: string;
  color: string;
  button_text: string;
  redirect_url: string;
  mobile_url?: string;
  is_sponsored: boolean;
}

const getBankColors = (color: string) => {
  switch (color) {
    case 'red':
      return {
        bg: 'bg-red-600',
        bgHover: 'hover:bg-red-700',
        bgGradient: 'bg-gradient-to-r from-red-500 to-red-600',
        text: 'text-red-600',
        border: 'border-red-600',
        bgLight: 'bg-red-50'
      };
    case 'green':
      return {
        bg: 'bg-green-600',
        bgHover: 'hover:bg-green-700',
        bgGradient: 'bg-gradient-to-r from-green-500 to-green-600',
        text: 'text-green-600',
        border: 'border-green-600',
        bgLight: 'bg-green-50'
      };
    case 'purple':
      return {
        bg: 'bg-purple-600',
        bgHover: 'hover:bg-purple-700',
        bgGradient: 'bg-gradient-to-r from-purple-500 to-purple-600',
        text: 'text-purple-600',
        border: 'border-purple-600',
        bgLight: 'bg-purple-50'
      };
    case 'blue':
    default:
      return {
        bg: 'bg-blue-600',
        bgHover: 'hover:bg-blue-700',
        bgGradient: 'bg-gradient-to-r from-blue-500 to-blue-600',
        text: 'text-blue-600',
        border: 'border-blue-600',
        bgLight: 'bg-blue-50'
      };
  }
};

interface Credit {
  id: string;
  bank_id: string;
  product_name: string;
  description: string;
  icon_type: string;
  interest_rate: string;
  max_amount: string;
  features: string[];
  button_url: string;
  display_order: number;
  is_active: boolean;
}

interface CreditCard {
  id: string;
  bank_id: string;
  card_name: string;
  description: string;
  icon_type: string;
  annual_fee: string;
  cashback: string;
  features: string[];
  button_url: string;
  display_order: number;
  is_active: boolean;
}

const BankDetailPage = () => {
  const { bankId } = useParams<{ bankId: string }>();
  const navigate = useNavigate();
  const [bank, setBank] = useState<Bank | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [heroCampaign, setHeroCampaign] = useState<Campaign | null>(null);
  const [credits, setCredits] = useState<Credit[]>([]);
  const [cards, setCards] = useState<CreditCard[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [activeTab, setActiveTab] = useState<'campaigns' | 'credits' | 'cards'>('campaigns');
  const [gtmClasses, setGtmClasses] = useState<Record<string, string>>({});

  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent;
      const isMobileDevice = /iPhone|iPod|Android/i.test(userAgent);
      const isTablet = /iPad/i.test(userAgent);
      setIsMobile(isMobileDevice || isTablet);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const fetchGTMClasses = async () => {
      if (!bankId) return;

      try {
        const pageSlug = `/banka/${bankId}`;
        const { data, error } = await supabase
          .from('page_conversion_tracking')
          .select('gtm_classes, enable_gtm')
          .eq('page_slug', pageSlug)
          .maybeSingle();

        if (!error && data && data.enable_gtm) {
          setGtmClasses(data.gtm_classes || {});
        }
      } catch (error) {
        console.error('Error fetching GTM classes:', error);
      }
    };

    fetchGTMClasses();
  }, [bankId]);

  useEffect(() => {
    const fetchBankData = async () => {
      if (!bankId) return;

      try {
        setIsLoading(true);

        const { data: bankData, error: bankError } = await supabase
          .from('bank_offers')
          .select('*')
          .eq('bank_id', bankId)
          .eq('is_active', true)
          .maybeSingle();

        if (bankError) {
          console.error('Error fetching bank:', bankError);
          return;
        }

        if (bankData) {
          setBank(bankData);
        }

        const { data: campaignsData, error: campaignsError } = await supabase
          .from('bank_campaigns')
          .select('*')
          .eq('bank_id', bankId)
          .eq('is_active', true)
          .order('display_order', { ascending: true });

        if (campaignsError) {
          console.error('Error fetching campaigns:', campaignsError);
          return;
        }

        if (campaignsData && campaignsData.length > 0) {
          const hero = campaignsData.find(c => c.is_hero);
          const otherCampaigns = campaignsData.filter(c => !c.is_hero);

          setHeroCampaign(hero || null);
          setCampaigns(otherCampaigns);
        }

        const { data: creditsData, error: creditsError } = await supabase
          .from('bank_credits')
          .select('*')
          .eq('bank_id', bankId)
          .eq('is_active', true)
          .order('display_order', { ascending: true });

        if (!creditsError && creditsData) {
          setCredits(creditsData);
        }

        const { data: cardsData, error: cardsError } = await supabase
          .from('bank_cards')
          .select('*')
          .eq('bank_id', bankId)
          .eq('is_active', true)
          .order('display_order', { ascending: true });

        if (!cardsError && cardsData) {
          setCards(cardsData);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBankData();
  }, [bankId]);

  const pushGTMEvent = (bankName: string, bankId: string, buttonType: string = 'main') => {
    if (typeof window !== 'undefined' && (window as any).dataLayer) {
      (window as any).dataLayer.push({
        event: 'bank_button_click',
        bank_name: bankName,
        bank_id: bankId,
        page: `banka/${bankId}`,
        button_type: buttonType
      });
      console.log('GTM Event pushed:', { event: 'bank_button_click', bank_name: bankName, bank_id: bankId, button_type: buttonType });
    }
  };

  const handleApply = (buttonType: string = 'main') => {
    if (!bank) return;

    // Push GTM event
    pushGTMEvent(bank.bank_name, bank.bank_id, buttonType);

    // Track page conversion
    trackPageConversion(`/banka/${bankId}`);

    const redirectUrl = isMobile && bank.mobile_url ? bank.mobile_url : bank.redirect_url;

    if (redirectUrl) {
      window.open(redirectUrl, '_blank', 'noopener,noreferrer');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F7BA45] mx-auto mb-4"></div>
          <p className="text-slate-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!bank) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle size={48} className="text-slate-400 mx-auto mb-4" />
          <p className="text-slate-600">Banka bulunamadı</p>
        </div>
      </div>
    );
  }

  const colors = getBankColors(bank.color);

  return (
    <div className="min-h-screen bg-slate-50">
      <button
        onClick={() => navigate(-1)}
        className="container mx-auto pt-4 px-4 flex items-center gap-2 text-slate-600 hover:text-slate-800 transition-colors"
      >
        <ArrowLeft size={20} />
        <span>Geri Dön</span>
      </button>

      <div className="container mx-auto py-6 px-4">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-6">
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center gap-4 mb-6">
              <img
                src={bank.logo_url}
                alt={bank.bank_name}
                className="h-12 sm:h-16 object-contain"
              />
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">{bank.bank_name}</h1>
                  <div className="flex items-center gap-0.5">
                    {[...Array(5)].map((_, index) => (
                      <StarFilled
                        key={index}
                        size={16}
                        className={colors.text}
                      />
                    ))}
                  </div>
                </div>
                <p className="text-slate-600">{bank.slogan}</p>
              </div>
            </div>

            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
              <button
                onClick={() => setActiveTab('campaigns')}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-sm sm:text-base font-medium transition-all whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'campaigns'
                    ? `${colors.bg} text-white shadow-lg`
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <Gift size={16} className="sm:w-[18px] sm:h-[18px]" />
                <span>Kampanyalar</span>
              </button>
              <button
                onClick={() => setActiveTab('credits')}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-sm sm:text-base font-medium transition-all whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'credits'
                    ? `${colors.bg} text-white shadow-lg`
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <Wallet size={16} className="sm:w-[18px] sm:h-[18px]" />
                <span>Krediler</span>
              </button>
              <button
                onClick={() => setActiveTab('cards')}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-sm sm:text-base font-medium transition-all whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'cards'
                    ? `${colors.bg} text-white shadow-lg`
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <CreditCard size={16} className="sm:w-[18px] sm:h-[18px]" />
                <span>Kredi Kartları</span>
              </button>
            </div>
          </div>

          {activeTab === 'campaigns' && heroCampaign && (
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="flex flex-col md:flex-row">
                {heroCampaign.campaign_image && (
                  <div className="md:w-2/5 lg:w-1/2 bg-slate-50 flex items-center justify-center p-4">
                    <img
                      src={heroCampaign.campaign_image}
                      alt={heroCampaign.title}
                      className="w-full h-auto max-h-[400px] object-contain"
                    />
                  </div>
                )}

                <div className="md:w-3/5 lg:w-1/2 p-8 md:p-10 flex flex-col justify-center">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Gift size={20} className={colors.text} />
                      <span className={`${colors.text} font-semibold text-xs uppercase tracking-wide`}>
                        Öne Çıkan Kampanya
                      </span>
                    </div>
                    {heroCampaign.end_date && (
                      <div className="flex items-center gap-2 text-slate-500">
                        <Calendar size={16} />
                        <span className="text-sm">Bitiş: {heroCampaign.end_date}</span>
                      </div>
                    )}
                  </div>

                  <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
                    {heroCampaign.title}
                  </h2>

                  <p className="text-slate-600 text-base mb-6 leading-relaxed">
                    {heroCampaign.description}
                  </p>

                  {heroCampaign.offer_details && heroCampaign.offer_details.length > 0 && (
                    <div className="space-y-2 mb-6">
                      {heroCampaign.offer_details.map((detail: string, index: number) => (
                        <div key={index} className="flex items-start gap-2">
                          <CheckCircle2 size={16} className={`${colors.text} flex-shrink-0 mt-0.5`} />
                          <span className="text-slate-700 text-sm">{detail}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    id={`bank-detail-${bankId}-hero`}
                    data-bank-name={bank.bank_name}
                    onClick={() => handleApply('hero')}
                    className={`${gtmClasses[bank.bank_id] || ''} ${colors.bgGradient} text-white px-8 py-4 rounded-lg text-lg font-semibold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center gap-3 w-fit`}
                  >
                    <span>{bank.button_text}</span>
                    <ArrowRight size={22} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {activeTab === 'campaigns' && campaigns.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-2xl font-bold text-slate-800 mb-4">Diğer Kampanyalar</h3>

            {campaigns.map((campaign) => (
              <div
                key={campaign.id}
                className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0">
                    <img
                      src={campaign.campaign_image || "/image copy copy.png"}
                      alt={campaign.title}
                      className="w-full h-full object-cover rounded"
                    />
                  </div>

                  <div className="flex-1 min-w-0 pr-2">
                    <h4 className="text-sm sm:text-base md:text-lg font-bold text-slate-800 leading-tight break-words line-clamp-2">
                      {campaign.title}
                    </h4>
                  </div>

                  <button
                    id={`bank-detail-${bankId}-campaign-${campaign.display_order}`}
                    data-bank-name={bank.bank_name}
                    onClick={() => handleApply(`campaign-${campaign.display_order}`)}
                    className={`${gtmClasses[bank.bank_id] || ''} ${colors.bgGradient} text-white px-4 sm:px-5 py-2 sm:py-2.5 rounded-lg font-semibold text-xs sm:text-sm shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-1.5 sm:gap-2 flex-shrink-0`}
                  >
                    <span>Başvur</span>
                    <ArrowRight size={14} className="sm:w-4 sm:h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'campaigns' && !heroCampaign && campaigns.length === 0 && (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <Gift size={48} className="text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600 text-lg mb-6">
              Bu banka için henüz kampanya bulunmamaktadır.
            </p>
            <button
              id={`bank-detail-${bankId}-no-campaigns`}
              data-bank-name={bank.bank_name}
              onClick={() => handleApply('no-campaigns')}
              className={`${gtmClasses[bank.bank_id] || ''} ${colors.bgGradient} text-white px-8 py-4 rounded-lg font-semibold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 inline-flex items-center gap-2`}
            >
              <span>{bank.button_text}</span>
              <ArrowRight size={20} />
            </button>
          </div>
        )}

        {activeTab === 'credits' && (
          <div className="space-y-4">
            <h3 className="text-xl sm:text-2xl font-bold text-slate-800 mb-4">Kredi Ürünleri</h3>

{credits.length > 0 ? (
              credits.map((credit) => (
                <div key={credit.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 bg-slate-100 rounded-lg flex items-center justify-center">
                      <Wallet size={32} className={colors.text} />
                    </div>

                    <div className="flex-1 min-w-0 pr-2">
                      <h4 className="text-sm sm:text-base md:text-lg font-bold text-slate-800 leading-tight mb-1">
                        {credit.product_name}
                      </h4>
                      <p className="text-xs sm:text-sm text-slate-600">
                        {credit.description}
                      </p>
                      {credit.interest_rate && (
                        <p className="text-xs text-slate-500 mt-1">Faiz: {credit.interest_rate}</p>
                      )}
                      {credit.max_amount && (
                        <p className="text-xs text-slate-500">Maksimum: {credit.max_amount}</p>
                      )}
                    </div>

                    <button
                      id={`bank-detail-${bankId}-credit-${credit.display_order}`}
                      data-bank-name={bank.bank_name}
                      onClick={() => {
                        if (credit.button_url) {
                          pushGTMEvent(bank.bank_name, bank.bank_id, `credit-${credit.display_order}`);
                          window.open(credit.button_url, '_blank', 'noopener,noreferrer');
                        } else {
                          handleApply(`credit-${credit.display_order}`);
                        }
                      }}
                      className={`${gtmClasses[bank.bank_id] || ''} ${colors.bgGradient} text-white px-4 sm:px-5 py-2 sm:py-2.5 rounded-lg font-semibold text-xs sm:text-sm shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-1.5 sm:gap-2 flex-shrink-0`}
                    >
                      <span>Başvur</span>
                      <ArrowRight size={14} className="sm:w-4 sm:h-4" />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                <Wallet size={48} className="text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600 text-lg mb-6">
                  Bu banka için henüz kredi ürünü bulunmamaktadır.
                </p>
                <button
                  id={`bank-detail-${bankId}-no-credits`}
                  data-bank-name={bank.bank_name}
                  onClick={() => handleApply('no-credits')}
                  className={`${gtmClasses[bank.bank_id] || ''} ${colors.bgGradient} text-white px-8 py-4 rounded-lg font-semibold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 inline-flex items-center gap-2`}
                >
                  <span>{bank.button_text}</span>
                  <ArrowRight size={20} />
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'cards' && (
          <div className="space-y-4">
            <h3 className="text-xl sm:text-2xl font-bold text-slate-800 mb-4">Kredi Kartları</h3>

{cards.length > 0 ? (
              cards.map((card) => (
                <div key={card.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 bg-slate-100 rounded-lg flex items-center justify-center">
                      <CreditCard size={32} className={colors.text} />
                    </div>

                    <div className="flex-1 min-w-0 pr-2">
                      <h4 className="text-sm sm:text-base md:text-lg font-bold text-slate-800 leading-tight mb-1">
                        {card.card_name}
                      </h4>
                      <p className="text-xs sm:text-sm text-slate-600">
                        {card.description}
                      </p>
                      {card.annual_fee && (
                        <p className="text-xs text-slate-500 mt-1">Yıllık Ücret: {card.annual_fee}</p>
                      )}
                      {card.cashback && (
                        <p className="text-xs text-slate-500">Cashback: {card.cashback}</p>
                      )}
                    </div>

                    <button
                      id={`bank-detail-${bankId}-card-${card.display_order}`}
                      data-bank-name={bank.bank_name}
                      onClick={() => {
                        if (card.button_url) {
                          pushGTMEvent(bank.bank_name, bank.bank_id, `card-${card.display_order}`);
                          window.open(card.button_url, '_blank', 'noopener,noreferrer');
                        } else {
                          handleApply(`card-${card.display_order}`);
                        }
                      }}
                      className={`${gtmClasses[bank.bank_id] || ''} ${colors.bgGradient} text-white px-4 sm:px-5 py-2 sm:py-2.5 rounded-lg font-semibold text-xs sm:text-sm shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-1.5 sm:gap-2 flex-shrink-0`}
                    >
                      <span>Başvur</span>
                      <ArrowRight size={14} className="sm:w-4 sm:h-4" />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                <CreditCard size={48} className="text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600 text-lg mb-6">
                  Bu banka için henüz kredi kartı bulunmamaktadır.
                </p>
                <button
                  id={`bank-detail-${bankId}-no-cards`}
                  data-bank-name={bank.bank_name}
                  onClick={() => handleApply('no-cards')}
                  className={`${gtmClasses[bank.bank_id] || ''} ${colors.bgGradient} text-white px-8 py-4 rounded-lg font-semibold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 inline-flex items-center gap-2`}
                >
                  <span>{bank.button_text}</span>
                  <ArrowRight size={20} />
                </button>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default BankDetailPage;
